import React from 'react';
import { ParsedInvestment } from '../services/api';
import { annualToMonthlyRate, describeRate } from '../utils/investmentRate';
import './QuickEntry.css';
import { formatBRL } from '../utils/format';

/**
 * O card de confirmação de uma aplicação interpretada.
 *
 * Vive fora do campo único porque os DOIS campos únicos chegam nele: o de
 * investimento (tela de Investimentos) e o de lançamento ("investi 5 mil em
 * CDB" escrito no campo de gasto). Duas cópias divergiriam, e é justamente
 * numa delas que a pergunta "esse dinheiro saiu da conta?" seria esquecida.
 *
 * As duas perguntas existem porque errar qualquer uma estraga um número que
 * ela confia:
 *
 * 1. **O dinheiro está saindo da conta agora?** "investi 500" sai; "tenho 5 mil
 *    no CDB" não. Criar transação nos dois casos faria o caixa despencar por
 *    aportes que nunca aconteceram.
 * 2. **A taxa é ao mês ou ao ano?** 6% ao mês é doze vezes 6% ao ano. Sem o
 *    período declarado, o app não converte — pergunta.
 */

/** O que ainda impede de gravar. Exportado porque o pai desabilita o botão. */
export const investmentIncomplete = (inv: ParsedInvestment | null): boolean =>
  !inv || inv.amount == null || inv.amount <= 0 ||
  (inv.operation === 'contribute' && !inv.investmentId) ||
  (inv.ambiguous.length > 0 && !inv.investmentId);

interface Props {
  inv: ParsedInvestment;
  onChange: (next: ParsedInvestment) => void;
  onSave: () => void;
  onCancel: () => void;
  salvando: boolean;
  /** Em qual período ela digita a taxa que faltou. */
  basePeriodo: 'monthly' | 'annual';
  onBasePeriodo: (p: 'monthly' | 'annual') => void;
}

/*
 * Este `fmt` NÃO obedece ao olhinho, e a exceção é deliberada.
 *
 * É a tela de confirmar o que a pessoa ACABOU de escrever: "lançar R$ 187,40
 * no débito?". Esconder o número aqui tornaria a confirmação impossível — ela
 * estaria dizendo sim para um valor que não pode ler. A regra é a mesma do
 * campo de ajustar saldo: valor que está sendo digitado não se esconde.
 *
 * Mas a FORMATAÇÃO vem do dono único: era mais uma cópia do `Intl`.
 */
const fmt = (v: number) => formatBRL(v);

const InvestmentProposal: React.FC<Props> = ({
  inv, onChange, onSave, onCancel, salvando, basePeriodo, onBasePeriodo,
}) => {
  const incompleto = investmentIncomplete(inv);
  const aporte = inv.operation === 'contribute';

  return (

        <div className="qe-card">
          <div className="qe-linha">
            <span className="qe-desc">{inv.name}</span>
            <span className="qe-valor">
              {inv.amount != null ? fmt(inv.amount) : <em>sem valor</em>}
            </span>
          </div>

          <div className="qe-chips">
            <span className="qe-chip">
              {inv.operation === 'contribute' ? 'Aporte' : 'Nova aplicação'}
            </span>
            <span className="qe-chip">{inv.type}</span>
            {inv.monthlyRate != null && (
              <span className="qe-chip">{describeRate(inv.monthlyRate)}</span>
            )}
            {aporte && inv.currentAmount != null && inv.amount != null && (
              <span className="qe-chip">
                {fmt(inv.currentAmount)} → {fmt(inv.movesCash ? inv.currentAmount + inv.amount : inv.amount)}
              </span>
            )}
          </div>

          {/* Nome que casou com mais de uma aplicação: pergunta, nunca chute. */}
          {inv.ambiguous.length > 0 && !inv.investmentId && (
            <div className="qe-gap">
              <div className="qe-gap-pergunta">
                Em qual delas?
                <span className="qe-gap-evidencia">{inv.ambiguous.length} aplicações com esse nome</span>
              </div>
              <div className="qe-opcoes">
                {inv.ambiguous.map(c => (
                  <button
                    key={c.id}
                    className="qe-opcao"
                    onClick={() => onChange({
                      ...inv,
                      operation: 'contribute',
                      investmentId: c.id,
                      name: c.name,
                      type: c.type,
                      currentAmount: c.amount,
                      currentMonthlyRate: c.monthlyRate,
                      monthlyRate: inv.monthlyRate ?? c.monthlyRate,
                      ambiguous: [],
                      missing: inv.missing.filter(m => m !== 'investmentId'),
                    })}
                  >
                    {c.name} · {fmt(c.amount)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(inv.amount == null || inv.amount <= 0) && (
            <div className="qe-gap">
              <div className="qe-gap-pergunta">
                {inv.operation === 'contribute' ? 'Quanto você aportou?' : 'Quanto foi aplicado?'}
              </div>
              <div className="qe-opcoes">
                <input
                  className="qe-criar-input"
                  style={{ width: 140 }}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="valor"
                  autoFocus
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    onChange({ ...inv, amount: Number.isFinite(v) && v > 0 ? v : null });
                  }}
                />
              </div>
            </div>
          )}

          {/* A pergunta que separa aporte de rendimento. Errar aqui é o que faz
              o caixa despencar a cada rendimento anotado.

              Em aporte, as duas opções também mudam o que o número SIGNIFICA:
              somar ao saldo, ou passar a ser o saldo. Por isso os rótulos
              dizem o resultado em vez de "sim/não" — a diferença entre
              620 → 1.120 e 620 → 500 não pode depender de interpretação. */}
          <div className="qe-gap">
            <div className="qe-gap-pergunta">
              {aporte ? 'O que é esse valor?' : 'O dinheiro está saindo da conta agora?'}
              <span className="qe-gap-evidencia">
                {inv.movesCash
                  ? 'vai virar um lançamento e reduzir seu caixa'
                  : 'só registra o saldo, seu caixa não muda'}
              </span>
            </div>
            <div className="qe-opcoes">
              <button
                className={`qe-opcao${inv.movesCash ? ' sugerida' : ''}`}
                onClick={() => onChange({ ...inv, movesCash: true })}
              >{aporte ? 'aportei agora (soma ao saldo)' : 'apliquei agora'}</button>
              <button
                className={`qe-opcao${!inv.movesCash ? ' sugerida' : ''}`}
                onClick={() => onChange({ ...inv, movesCash: false })}
              >{aporte ? 'é o saldo atual (corrige o valor)' : 'já estava aplicado'}</button>
            </div>
          </div>

          {/* Taxa: opcional, mas é ela que destrava a projeção. */}
          {inv.monthlyRate == null && (
            <div className="qe-gap">
              <div className="qe-gap-pergunta">
                Quanto rende? <em style={{ fontWeight: 400 }}>(opcional)</em>
                <span className="qe-gap-evidencia">
                  {inv.rateText
                    ? `você escreveu "${inv.rateText}", mas o app não converte índice: informe a taxa equivalente`
                    : 'sem taxa, esta aplicação não entra na projeção de rendimento'}
                </span>
              </div>
              <div className="qe-opcoes">
                <input
                  className="qe-criar-input"
                  style={{ width: 90 }}
                  type="number"
                  min="0"
                  step="0.0001"
                  placeholder="%"
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    if (!Number.isFinite(v) || v < 0) { onChange({ ...inv, monthlyRate: null, rateOriginal: null }); return; }
                    onChange({
                      ...inv,
                      rateOriginal: v,
                      rateBasis: basePeriodo,
                      monthlyRate: basePeriodo === 'annual' ? annualToMonthlyRate(v) : v,
                    });
                  }}
                />
                <button
                  className={`qe-opcao${basePeriodo === 'annual' ? ' sugerida' : ''}`}
                  onClick={() => {
                    onBasePeriodo('annual');
                    if (inv.rateOriginal != null) {
                      onChange({ ...inv, rateBasis: 'annual', monthlyRate: annualToMonthlyRate(inv.rateOriginal) });
                    }
                  }}
                >ao ano</button>
                <button
                  className={`qe-opcao${basePeriodo === 'monthly' ? ' sugerida' : ''}`}
                  onClick={() => {
                    onBasePeriodo('monthly');
                    if (inv.rateOriginal != null) {
                      onChange({ ...inv, rateBasis: 'monthly', monthlyRate: inv.rateOriginal });
                    }
                  }}
                >ao mês</button>
              </div>
            </div>
          )}

          <div className="qe-acoes">
            <button className="qe-salvar" onClick={onSave} disabled={incompleto || salvando} aria-busy={salvando}>
              {incompleto ? 'Complete acima'
                : inv.operation === 'contribute' ? `Aportar em ${inv.name}`
                : 'Registrar aplicação'}
            </button>
            <button className="qe-cancelar" onClick={onCancel} disabled={salvando}>Cancelar</button>
          </div>
        </div>
  );
};

export default InvestmentProposal;
